import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [loading, setLoading] = useState(true);

  const fetchMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (err?.response && err.response.status !== 401) {
        console.warn("[auth] fetchMe failed", err);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMe(); }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const loginEmail = async (email) => {
    const { data } = await api.post("/auth/login-email", { email });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const sendMagicLink = async (email) => {
    const { data } = await api.post("/auth/magic-link", { email });
    return data;
  };

  const consumeMagic = async (token) => {
    const { data } = await api.post("/auth/consume-magic", { token });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("[auth] logout call failed", err); }
    localStorage.removeItem("access_token");
    setUser(false);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: fetchMe, sendMagicLink, consumeMagic, loginEmail }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
